import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  FolderOpen, 
  MoreVertical, 
  Loader2, 
  AlertTriangle, 
  User, 
  LogOut, 
  Settings, 
  ChevronDown 
} from 'lucide-react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { ref, onValue, get } from 'firebase/database';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, firestore as db, realtimeDb as rtdb } from './firebase';
import { useProcess } from './ProcessContext';
import { Button } from './components/Button';
import { Card } from './components/Card';

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  // Dropdown state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // 1. Authenticate & Fetch Project List from Firestore
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        fetchProjects(currentUser.uid);
      } else {
        navigate('/auth');
      }
    });

    return () => unsubscribeAuth();
  }, [navigate]);

  // 2. Handle clicking outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/auth');
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const fetchProjects = async (userId) => {
    try {
      setLoading(true);
      setError(null);

      // Firestore Query
      const q = query(
        collection(db, "projects"),
        where("userId", "==", userId),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);

      const projectList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setProjects(projectList);
    } catch (err) {
      console.error("Firestore Error:", err);
      if (err.code === 'failed-precondition') {
        setError("Missing Index: Check browser console for the Firebase link creation.");
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          
          {/* Left Side: Logo Only */}
          <div className="flex items-center gap-3">
            <img
                src="/logo512.png"
                alt="BloxBuddy"
                className="w-10 h-10 rounded-lg object-contain bg-slate-900 border border-slate-800 shadow-lg shadow-blue-900/20"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
            <div className="w-10 h-10 bg-blue-600 rounded-lg hidden items-center justify-center shadow-lg shadow-blue-900/50">
              <span className="font-bold text-white">B</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight">BloxBuddy Studio</h1>
          </div>

          {/* Right Side: New Project Button + Profile Dropdown */}
          <div className="flex items-center gap-4 w-full md:w-auto">
            
            <button
              onClick={() => navigate('/console/new')}
              className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-lg font-bold shadow-lg shadow-blue-900/30 transition-all flex items-center justify-center gap-2"
            >
              <Plus size={20} /> New Project
            </button>

            <div className="h-8 w-px bg-slate-800 hidden md:block" />

            {/* Account Dropdown (Now on far right) */}
            <div className="relative" ref={menuRef}>
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center gap-2 p-1 rounded-full bg-slate-900 border border-slate-800 hover:border-slate-700 transition-all shadow-lg hover:shadow-blue-900/10"
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-sm font-bold shadow-inner">
                  {user?.displayName ? user.displayName[0].toUpperCase() : <User size={18} />}
                </div>
                <ChevronDown size={14} className={`text-slate-500 mr-2 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu - Aligned to right */}
              {isMenuOpen && (
                <div className="absolute right-0 mt-3 w-64 bg-slate-900 border border-slate-800 rounded-xl shadow-2xl z-50 py-2 animate-in fade-in zoom-in duration-150 origin-top-right backdrop-blur-xl">
                  <div className="px-4 py-3 border-b border-slate-800 mb-2">
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Signed in as</p>
                    <p className="text-sm font-bold truncate text-white mt-0.5">{user?.email}</p>
                  </div>
                  
                  <button 
                    onClick={() => { navigate('/settings'); setIsMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                  >
                    <Settings size={16} className="text-slate-500" /> Account Settings
                  </button>
                  
                  <button 
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-red-950/20 hover:text-red-300 transition-colors"
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Error Banner */}
        {error && (
          <div className="bg-red-900/20 border border-red-500/50 text-red-200 p-4 rounded-xl mb-8 flex items-center gap-3">
            <AlertTriangle size={24} />
            <div>
              <p className="font-bold">Database Error</p>
              <p className="text-sm font-mono">{error}</p>
            </div>
          </div>
        )}

        {/* Project Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {loading ? (
             <div className="text-slate-500 col-span-full flex flex-col items-center py-20 gap-4">
               <Loader2 className="animate-spin text-blue-500" size={32} />
               <p>Loading Projects...</p>
             </div>
          ) : projects.length === 0 ? (
             <div className="text-slate-500 col-span-full text-center py-20 border-2 border-dashed border-slate-800 rounded-xl">
               <p className="text-lg mb-2">No projects found.</p>
               <p className="text-sm text-slate-600">Create one to get started.</p>
             </div>
          ) : (
             projects.map((project) => (
                <LiveProjectCard key={project.id} project={project} />
             ))
          )}
        </div>
      </div>
    </div>
  );
}

// --- CHILD COMPONENT: OPTIMIZED LISTENERS ---
function LiveProjectCard({ project }) {
    const navigate = useNavigate();
   // const { activeProjectId } = useProcess();

    const [liveData, setLiveData] = useState({
        status: 'idle',
        lastUpdated: project.createdAt ? project.createdAt.toMillis?.() : Date.now(),
        thumbnail: null,
        title: project.title || "Loading..."
    });

    useEffect(() => {
        const basePath = `projects/${project.id}`;

        const statusUnsub = onValue(ref(rtdb, `${basePath}/info/status`), (snap) => {
            const val = snap.val();
            if (val) setLiveData(prev => ({ ...prev, status: val }));
        });

        get(ref(rtdb, `${basePath}/info/title`))
          .then((snap) => {
            const val = snap && snap.exists() ? snap.val() : null;
            if (val) setLiveData(prev => ({ ...prev, title: val }));
          })
          .catch(err => {
            console.warn(`Failed to read title for project ${project.id}:`, err);
          });

        const timeUnsub = onValue(ref(rtdb, `${basePath}/info/lastUpdated`), (snap) => {
            const val = snap.val();
            if (val) setLiveData(prev => ({ ...prev, lastUpdated: val }));
        });

        get(ref(rtdb, `${basePath}/thumbnail/url`))
          .then((snap) => {
            const val = snap && snap.exists() ? snap.val() : null;
            if (val) setLiveData(prev => ({ ...prev, thumbnail: val }));
          })
          .catch(err => {
            console.warn(`Failed to read thumbnail for project ${project.id}:`, err);
          });

        return () => {
            try { statusUnsub(); } catch (e) { /* ignore */ }
            try { timeUnsub(); } catch (e) { /* ignore */ }
        };
    }, [project.id]);

    const handleOpenProject = () => {
        navigate(`/console/project/${project.id}`);
    };

    const handleMenuClick = (e) => {
        e.stopPropagation();
    };

    const dbStatus = (liveData.status || '').toLowerCase();
    const isWorking =
      //  activeProjectId === project.id ||
        (dbStatus !== 'idle' && dbStatus !== 'complete' && dbStatus !== 'error' && dbStatus !== '');

    const getTimeAgo = (timestamp) => {
        if (!timestamp) return 'Unknown';
        const seconds = Math.floor((Date.now() - timestamp) / 1000);
        if (seconds < 60) return 'Just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        return `${Math.floor(hours / 24)}d ago`;
    };

    return (
        <Card
            onClick={handleOpenProject}
            className={`
                bg-slate-900 border transition-all cursor-pointer group relative overflow-hidden shadow-sm hover:shadow-blue-900/10 flex flex-col justify-between h-full
                ${isWorking ? 'border-amber-500/30' : 'border-slate-800 hover:border-blue-500/50 hover:bg-slate-900/80'}
            `}
        >
            <div className="absolute top-0 right-0 p-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={handleMenuClick} className="text-slate-400 hover:text-white bg-slate-950/50 rounded p-1 backdrop-blur-sm">
                    <MoreVertical size={20} />
                </button>
            </div>

            <div className="flex items-start gap-4 mb-4">
                <div className={`
                    rounded-xl transition-colors shadow-inner overflow-hidden flex items-center justify-center shrink-0
                    ${isWorking
                        ? 'bg-amber-500/10 text-amber-500 p-3 w-14 h-14'
                        : liveData.thumbnail
                            ? 'w-14 h-14 bg-slate-950 border border-slate-700'
                            : 'bg-slate-800 text-blue-400 group-hover:text-blue-300 p-3 w-14 h-14'
                    }
                `}>
                    {isWorking ? (
                        <Loader2 size={24} className="animate-spin" />
                    ) : liveData.thumbnail ? (
                        <img src={liveData.thumbnail} alt="Thumbnail" className="w-full h-full object-cover" />
                    ) : (
                        <FolderOpen size={24} />
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-bold group-hover:text-blue-400 transition-colors truncate">
                        {liveData.title}
                    </h3>
                    <p className="text-slate-500 text-xs mt-1 flex items-center gap-1">
                        Edited {getTimeAgo(liveData.lastUpdated)}
                    </p>
                </div>
            </div>

            <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-800">
                {isWorking ? (
                    <span className="text-xs font-bold px-2 py-1 rounded border bg-amber-500/10 text-amber-500 border-amber-500/20 flex items-center gap-2 transition-all">
                        <Loader2 size={12} className="animate-spin" />
                        {dbStatus === 'idle' ? 'Working...' : liveData.status}
                    </span>
                ) : (
                    <span className="text-xs font-mono px-2 py-1 rounded border bg-slate-800/50 text-slate-500 border-slate-700 flex items-center gap-2 transition-all">
                        <div className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                        Idle
                    </span>
                )}

                <Button variant="ghost" className="text-xs h-8 hover:bg-slate-800 pointer-events-none">
                    Open Studio
                </Button>
            </div>
        </Card>
    );
}
